import type {
  AssessmentAggregateEvidence,
  AssessmentBlock,
  AssessmentBlockEvidence,
  AssessmentConfidence,
  AssessmentDiagnosis,
  AssessmentDiagnosisInput,
  AssessmentGoal,
  AssessmentOpportunity,
  AssessmentSeverity,
  AssessmentStrength,
  CreateAssessmentPlanOptions,
} from "./types";

const BASELINE_TEXT =
  "Clear communication starts with steady attention. A reliable typist keeps ideas moving, corrects small slips, and stays relaxed while the sentence develops naturally.";

const MECHANICS_TEXT =
  'Maya reviewed the Friday brief, added "Final Draft" to the title, and asked: Can Jordan send the updated notes before 4:30 p.m.? Yes, please include the charts.';

const SYMBOL_PROBES: Record<AssessmentGoal, string> = {
  writing:
    "Draft 3 includes 12 sources, 4 interviews, and a 750-word limit. Email edits@example.com; label the file article_v2-final, then note 35% complete.",
  gaming:
    "Squad 7 is ready: type /join #alpha, tag @support, and set volume to 65%. The next match starts at 8:45; score target = 2,500.",
  study:
    "Chapter 8 covers 3 models, 24 examples, and a 15% margin of error. Save notes as study_unit-04; review pages 118-126 before 9:00.",
  general:
    "Order 42 has 6 items and costs $73.50 before tax. Save the reference as task_2026-07; progress is 80%, and the due time is 5:30.",
};

const FLUENCY_TEXT =
  "Strong typing feels calm even when the work becomes more demanding. Read a few words ahead, keep a sustainable rhythm, and let accuracy guide the pace. When a difficult phrase appears, move through it deliberately instead of rushing the easy words and stopping at the hard ones. The goal is not a brief burst of speed; it is dependable flow from the beginning of the passage to the end.";

const ADAPTIVE_TEXT: Record<string, string> = {
  shiftkeys:
    'Riley asked, "Should Tuesday\'s Review include Q3 results?" The answer was Yes: add London, New York, and Sao Paulo before 2:15 p.m.',
  weakfingers:
    "Quiet people prepare proper questions. A quick proposal requires equal parts patience, quality, and precise follow-up across every project.",
  patterns:
    "The thoughtful team continued planning, testing, and improving the solution. Their preparation turned repeated information into confident action.",
  numbers:
    "Report 27 shows 14%, 38%, and 62% across groups A-C. Recheck rows 5-9, save version_03, and confirm the total equals $418.75.",
  accuracy:
    "Deliberate practice rewards clean first attempts. Slow down just enough to notice each letter, space, and mark before building speed again.",
  general:
    "A steady typist scans ahead, completes each phrase cleanly, and carries the same relaxed rhythm through familiar words and difficult combinations.",
};

function normalizeGoal(goal: AssessmentGoal | null | undefined): AssessmentGoal {
  return goal === "writing" ||
    goal === "gaming" ||
    goal === "study" ||
    goal === "general"
    ? goal
    : "general";
}

function focusFromSkillId(skillId: string | null | undefined): string {
  if (!skillId) {
    return "general";
  }
  if (skillId.startsWith("number:")) {
    return "numbers";
  }
  if (
    skillId.startsWith("shiftedKey:") ||
    skillId.startsWith("punctuation:")
  ) {
    return "shiftkeys";
  }
  if (skillId.startsWith("bigram:") || skillId.startsWith("trigram:")) {
    return "patterns";
  }
  if (/^key:[qazp;'[\]/]$/i.test(skillId)) {
    return "weakfingers";
  }
  return "general";
}

export function createAssessmentPlan(
  options: CreateAssessmentPlanOptions = {},
): AssessmentBlock[] {
  const goal = normalizeGoal(options.goal);
  const priorFocus = focusFromSkillId(options.priorOpportunitySkillId);

  return [
    {
      id: "baseline-prose",
      kind: "baseline",
      name: "Baseline prose",
      shortName: "Baseline",
      description:
        "Settle into your normal pace with natural mixed text. Do not sprint.",
      objective: "Establish an accurate, representative starting pace.",
      skillFocus: "general",
      text: BASELINE_TEXT,
    },
    {
      id: "mechanics",
      kind: "mechanics",
      name: "Capitalization & punctuation",
      shortName: "Mechanics",
      description:
        "Keep normal rhythm while handling capitals, quotes, punctuation, and a time.",
      objective: "Measure Shift coordination and punctuation control.",
      skillFocus: "shiftkeys",
      text: MECHANICS_TEXT,
    },
    {
      id: "symbols",
      kind: "symbols",
      name: "Numbers & symbols",
      shortName: "Symbols",
      description:
        "Type the numbers and symbols exactly as shown. This probe reflects your selected goal.",
      objective: "Measure practical number-row and symbol accuracy.",
      skillFocus: "numbers",
      text: SYMBOL_PROBES[goal],
    },
    {
      id: "adaptive-probe",
      kind: "adaptive",
      name: "Adaptive focus",
      shortName: "Adaptive",
      description:
        "This block targets the clearest opportunity in your evidence so far.",
      objective: "Confirm whether an early signal repeats with focused evidence.",
      skillFocus: priorFocus,
      text: ADAPTIVE_TEXT[priorFocus] ?? ADAPTIVE_TEXT.general!,
      isAdaptive: true,
    },
    {
      id: "sustained-fluency",
      kind: "fluency",
      name: "Sustained fluency",
      shortName: "Fluency",
      description:
        "Finish with a longer passage at a pace you can hold without forcing it.",
      objective: "Measure pace, accuracy, and rhythm under sustained work.",
      skillFocus: "rhythm",
      text: FLUENCY_TEXT,
    },
  ];
}

function valueOr(value: number | null, fallback: number): number {
  return value === null || !Number.isFinite(value) ? fallback : value;
}

export function selectAdaptiveAssessmentFocus(
  evidence: AssessmentBlockEvidence[],
  priorOpportunitySkillId?: string | null,
): string {
  const priorFocus = focusFromSkillId(priorOpportunitySkillId);
  const completed = evidence.filter((item) => item.printableAttempts > 0);
  if (completed.length === 0) {
    return priorFocus;
  }

  const weakest = [...completed].sort((left, right) => {
    const leftAccuracy = valueOr(left.accuracy, 100);
    const rightAccuracy = valueOr(right.accuracy, 100);
    if (leftAccuracy !== rightAccuracy) {
      return leftAccuracy - rightAccuracy;
    }
    return valueOr(left.consistency, 100) - valueOr(right.consistency, 100);
  })[0];

  if (!weakest) {
    return priorFocus;
  }
  if (weakest.kind === "mechanics") {
    return "shiftkeys";
  }
  if (weakest.kind === "symbols") {
    return "numbers";
  }
  if (weakest.kind === "baseline" && valueOr(weakest.accuracy, 100) < 94) {
    return "accuracy";
  }
  return weakest.skillFocus === "general" ? priorFocus : weakest.skillFocus;
}

export function createAdaptiveAssessmentBlock(
  evidence: AssessmentBlockEvidence[],
  priorOpportunitySkillId?: string | null,
): AssessmentBlock {
  const focus = selectAdaptiveAssessmentFocus(
    evidence,
    priorOpportunitySkillId,
  );
  const labels: Record<string, string> = {
    shiftkeys: "capitalization and punctuation",
    weakfingers: "outer-key control",
    patterns: "common pattern fluency",
    numbers: "number-row and symbol control",
    accuracy: "clean first-attempt accuracy",
    general: "steady mixed-text control",
  };

  return {
    id: "adaptive-probe",
    kind: "adaptive",
    name: "Adaptive focus",
    shortName: "Adaptive",
    description: `Your early evidence points to ${labels[focus] ?? labels.general}. This block checks whether that signal repeats.`,
    objective: "Confirm an early opportunity with focused evidence.",
    skillFocus: focus,
    text: ADAPTIVE_TEXT[focus] ?? ADAPTIVE_TEXT.general!,
    isAdaptive: true,
  };
}

function clamp(value: number, minimum = 0, maximum = 1): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function round(value: number, places = 0): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

function confidenceBand(score: number): AssessmentConfidence {
  if (score >= 0.76) {
    return "high";
  }
  if (score >= 0.46) {
    return "medium";
  }
  return "low";
}

function severityWeight(severity: AssessmentSeverity): number {
  return severity === "high" ? 90 : severity === "medium" ? 60 : 30;
}

function confidenceCopy(confidence: AssessmentConfidence): string {
  if (confidence === "high") {
    return "High confidence";
  }
  if (confidence === "medium") {
    return "Growing confidence";
  }
  return "Exploratory signal";
}

function blockConfidence(
  overallScore: number,
  sampleCount: number,
): { score: number; band: AssessmentConfidence } {
  const score = clamp(overallScore * 0.62 + clamp(sampleCount / 90) * 0.38);
  return { score, band: confidenceBand(score) };
}

function formatPercent(value: number | null): string {
  return `${Math.round(valueOr(value, 0))}%`;
}

function createOpportunity(options: {
  id: string;
  area: string;
  skill: string;
  severity: AssessmentSeverity;
  overallConfidence: number;
  sampleCount: number;
  evidence: string[];
  details: string;
  estimatedImpact: string;
  recommendation: string;
  professionalSkillFocus: string;
  professionalGeneralFocus?: string;
  gameDirective: string;
  deficit: number;
}): AssessmentOpportunity {
  const confidence = blockConfidence(
    options.overallConfidence,
    options.sampleCount,
  );

  return {
    id: options.id,
    area: options.area,
    skill: options.skill,
    severity: options.severity,
    confidence: confidence.band,
    confidenceScore: round(confidence.score, 3),
    sampleCount: options.sampleCount,
    evidence: options.evidence,
    details: options.details,
    estimatedImpact: options.estimatedImpact,
    recommendation: options.recommendation,
    professionalSkillFocus: options.professionalSkillFocus,
    professionalGeneralFocus:
      options.professionalGeneralFocus ?? "general",
    gameDirective: options.gameDirective,
    priority:
      severityWeight(options.severity) +
      Math.min(30, Math.max(0, options.deficit)) +
      confidence.score * 20,
  };
}

function findBlock(
  blocks: AssessmentBlockEvidence[],
  kind: AssessmentBlockEvidence["kind"],
): AssessmentBlockEvidence | undefined {
  return blocks.find((block) => block.kind === kind);
}

function assessmentConfidence(
  aggregate: AssessmentAggregateEvidence,
  blockCount: number,
): { score: number; band: AssessmentConfidence } {
  const durationEvidence = clamp(aggregate.activeDurationMs / 120_000);
  const attemptEvidence = clamp(aggregate.printableAttempts / 350);
  const blockEvidence = clamp(blockCount / 5);
  const validityEvidence = aggregate.valid ? 1 : 0.25;
  const score = clamp(
    durationEvidence * 0.38 +
      attemptEvidence * 0.28 +
      blockEvidence * 0.22 +
      validityEvidence * 0.12,
  );
  return { score, band: confidenceBand(score) };
}

function repeatedCharacterOpportunity(
  blocks: AssessmentBlockEvidence[],
  overallConfidence: number,
): AssessmentOpportunity | null {
  const combined = new Map<string, { correct: number; incorrect: number }>();

  for (const block of blocks) {
    for (const [character, evidence] of Object.entries(
      block.characterEvidence ?? {},
    )) {
      if (character === " " || character === "\n") {
        continue;
      }
      const current = combined.get(character) ?? { correct: 0, incorrect: 0 };
      current.correct += evidence.correct;
      current.incorrect += evidence.incorrect;
      combined.set(character, current);
    }
  }

  const candidate = [...combined.entries()]
    .map(([character, evidence]) => {
      const attempts = evidence.correct + evidence.incorrect;
      const accuracy =
        attempts > 0 ? (evidence.correct / attempts) * 100 : 100;
      return { character, attempts, accuracy, incorrect: evidence.incorrect };
    })
    .filter(
      (item) =>
        item.attempts >= 3 && item.incorrect >= 2 && item.accuracy < 90,
    )
    .sort((left, right) => {
      if (left.accuracy !== right.accuracy) {
        return left.accuracy - right.accuracy;
      }
      return right.incorrect - left.incorrect;
    })[0];

  if (!candidate) {
    return null;
  }

  const shifted = candidate.character.toUpperCase() === candidate.character &&
    candidate.character.toLowerCase() !== candidate.character;
  return createOpportunity({
    id: `character-${candidate.character.codePointAt(0) ?? 0}`,
    area: `Key “${candidate.character}”`,
    skill: shifted ? "Shifted-key control" : "Key accuracy",
    severity: candidate.accuracy < 70 ? "high" : "medium",
    overallConfidence,
    sampleCount: candidate.attempts,
    evidence: [
      `${candidate.incorrect} initial errors in ${candidate.attempts} observed attempts`,
      `${Math.round(candidate.accuracy)}% first-attempt accuracy`,
    ],
    details:
      "This key produced repeated first-attempt errors during the diagnostic. Treat it as a focused practice signal, not a permanent weakness.",
    estimatedImpact:
      "Cleaner first attempts reduce corrections and protect typing rhythm.",
    recommendation:
      "Use a short targeted passage that repeats this key inside real words, then reassess after several sessions.",
    professionalSkillFocus: shifted ? "shiftkeys" : "patterns",
    gameDirective: `Precision wave: prioritize words containing “${candidate.character}”.`,
    deficit: 95 - candidate.accuracy,
  });
}

function strengthFromBlock(
  block: AssessmentBlockEvidence,
  overallWpm: number | null,
): AssessmentStrength | null {
  const accuracy = valueOr(block.accuracy, 0);
  const pace = valueOr(block.accurateWpm, 0);
  const overallPace = valueOr(overallWpm, pace);
  if (
    block.printableAttempts < 20 ||
    accuracy < 97 ||
    pace < overallPace * 0.9
  ) {
    return null;
  }
  return {
    id: `strength-${block.blockId}`,
    label: `${block.name} held steady`,
    evidence: `${formatPercent(block.accuracy)} accuracy across ${block.printableAttempts} attempts.`,
  };
}

export function buildAssessmentDiagnosis(
  input: AssessmentDiagnosisInput,
): AssessmentDiagnosis {
  const completedBlocks = input.blocks.filter(
    (block) => block.printableAttempts > 0,
  );
  const confidence = assessmentConfidence(
    input.aggregate,
    completedBlocks.length,
  );
  const baseline = findBlock(completedBlocks, "baseline");
  const mechanics = findBlock(completedBlocks, "mechanics");
  const symbols = findBlock(completedBlocks, "symbols");
  const adaptive = findBlock(completedBlocks, "adaptive");
  const fluency = findBlock(completedBlocks, "fluency");
  const opportunities: AssessmentOpportunity[] = [];
  const baselineAccuracy = valueOr(
    baseline?.accuracy ?? null,
    valueOr(input.aggregate.accuracy, 100),
  );
  const baselineWpm = valueOr(
    baseline?.accurateWpm ?? null,
    valueOr(input.aggregate.accurateWpm, 0),
  );
  const overallAccuracy = valueOr(input.aggregate.accuracy, 100);

  if (overallAccuracy < 96) {
    opportunities.push(
      createOpportunity({
        id: "first-attempt-accuracy",
        area: "First-attempt accuracy",
        skill: "Accuracy control",
        severity:
          overallAccuracy < 90
            ? "high"
            : overallAccuracy < 94
              ? "medium"
              : "low",
        overallConfidence: confidence.score,
        sampleCount: input.aggregate.printableAttempts,
        evidence: [
          `${formatPercent(input.aggregate.accuracy)} across the full diagnostic`,
          `${input.aggregate.correctPrintableAttempts} correct attempts out of ${input.aggregate.printableAttempts}`,
        ],
        details:
          "Corrections are interrupting otherwise useful typing evidence. A small pace reduction should improve usable speed more than pushing harder.",
        estimatedImpact:
          "Higher first-attempt accuracy improves net speed and makes rhythm more dependable.",
        recommendation:
          "Run an accuracy-focused Professional session at a pace that keeps first attempts clean.",
        professionalSkillFocus: "general",
        professionalGeneralFocus: "accuracy",
        gameDirective:
          "Precision defense: use relaxed pressure and protect a clean combo.",
        deficit: 96 - overallAccuracy,
      }),
    );
  }

  if (mechanics) {
    const mechanicsAccuracy = valueOr(mechanics.accuracy, 100);
    const drop = baselineAccuracy - mechanicsAccuracy;
    if (mechanicsAccuracy < 94 || drop >= 3) {
      opportunities.push(
        createOpportunity({
          id: "capitalization-punctuation",
          area: "Capitalization & punctuation",
          skill: "Shift coordination",
          severity:
            mechanicsAccuracy < 85 || drop >= 10
              ? "high"
              : mechanicsAccuracy < 91 || drop >= 6
                ? "medium"
                : "low",
          overallConfidence: confidence.score,
          sampleCount: mechanics.printableAttempts,
          evidence: [
            `${formatPercent(mechanics.accuracy)} in the mechanics block`,
            drop > 0
              ? `${Math.round(drop)} points below baseline accuracy`
              : "No accuracy drop from baseline",
          ],
          details:
            "Mixed case and punctuation introduced more friction than ordinary prose.",
          estimatedImpact:
            "Smoother Shift and punctuation control helps writing, chat, and structured work stay uninterrupted.",
          recommendation:
            "Practice mixed-case sentences using both Shift keys and exact punctuation.",
          professionalSkillFocus: "shiftkeys",
          gameDirective:
            "Mechanics wave: enable exact case and prioritize capitalized targets.",
          deficit: Math.max(94 - mechanicsAccuracy, drop),
        }),
      );
    }
  }

  if (symbols) {
    const symbolAccuracy = valueOr(symbols.accuracy, 100);
    const drop = baselineAccuracy - symbolAccuracy;
    if (symbolAccuracy < 92 || drop >= 5) {
      opportunities.push(
        createOpportunity({
          id: "numbers-symbols",
          area: "Numbers & symbols",
          skill: "Number-row control",
          severity:
            symbolAccuracy < 80 || drop >= 14
              ? "high"
              : symbolAccuracy < 88 || drop >= 8
                ? "medium"
                : "low",
          overallConfidence: confidence.score,
          sampleCount: symbols.printableAttempts,
          evidence: [
            `${formatPercent(symbols.accuracy)} in the practical symbol probe`,
            drop > 0
              ? `${Math.round(drop)} points below baseline accuracy`
              : "No accuracy drop from baseline",
          ],
          details:
            "Practical numbers and symbols required more correction than ordinary prose.",
          estimatedImpact:
            "Improvement transfers to data entry, commands, references, dates, and technical work.",
          recommendation:
            "Use a short accuracy session containing dates, percentages, file names, and common symbols.",
          professionalSkillFocus: "general",
          professionalGeneralFocus: "accuracy",
          gameDirective:
            "Symbol control wave: relaxed pressure with exact-case matching.",
          deficit: Math.max(92 - symbolAccuracy, drop),
        }),
      );
    }
  }

  if (adaptive) {
    const adaptiveAccuracy = valueOr(adaptive.accuracy, 100);
    const adaptiveWpm = valueOr(adaptive.accurateWpm, baselineWpm);
    const paceRatio = baselineWpm > 0 ? adaptiveWpm / baselineWpm : 1;
    if (adaptiveAccuracy < 94 || paceRatio < 0.88) {
      const focusLabels: Record<string, string> = {
        shiftkeys: "Shift coordination",
        weakfingers: "Outer-key control",
        patterns: "Pattern fluency",
        numbers: "Number-row control",
        accuracy: "Clean first attempts",
        general: "Mixed-text control",
      };
      const focus =
        focusLabels[adaptive.skillFocus] ?? "Mixed-text control";
      opportunities.push(
        createOpportunity({
          id: `adaptive-${adaptive.skillFocus}`,
          area: focus,
          skill: "Confirmed adaptive signal",
          severity:
            adaptiveAccuracy < 84 || paceRatio < 0.72
              ? "high"
              : adaptiveAccuracy < 91 || paceRatio < 0.82
                ? "medium"
                : "low",
          overallConfidence: Math.min(1, confidence.score + 0.08),
          sampleCount: adaptive.printableAttempts,
          evidence: [
            `${formatPercent(adaptive.accuracy)} in the adaptive confirmation block`,
            baselineWpm > 0
              ? `${Math.round(adaptiveWpm)} WPM versus ${Math.round(baselineWpm)} baseline WPM`
              : `${Math.round(adaptiveWpm)} accurate WPM`,
          ],
          details:
            "The focused probe repeated an early signal, making it more useful than a one-off mistake.",
          estimatedImpact:
            "Targeting a repeated signal should produce a faster return than broad, untargeted repetition.",
          recommendation:
            "Use the matched Professional preset for several short sessions before reassessing.",
          professionalSkillFocus:
            adaptive.skillFocus === "numbers" ||
            adaptive.skillFocus === "accuracy"
              ? "general"
              : adaptive.skillFocus,
          professionalGeneralFocus:
            adaptive.skillFocus === "accuracy" ||
            adaptive.skillFocus === "numbers"
              ? "accuracy"
              : "general",
          gameDirective: `Adaptive wave: emphasize ${focus.toLowerCase()} at relaxed pressure.`,
          deficit: Math.max(94 - adaptiveAccuracy, (1 - paceRatio) * 100),
        }),
      );
    }
  }

  if (fluency) {
    const fluencyAccuracy = valueOr(fluency.accuracy, 100);
    const fluencyWpm = valueOr(fluency.accurateWpm, baselineWpm);
    const fluencyConsistency = valueOr(fluency.consistency, 100);
    const accuracyDrop = baselineAccuracy - fluencyAccuracy;
    const paceRatio = baselineWpm > 0 ? fluencyWpm / baselineWpm : 1;
    if (
      fluencyConsistency < 78 ||
      accuracyDrop >= 4 ||
      paceRatio < 0.88
    ) {
      opportunities.push(
        createOpportunity({
          id: "sustained-fluency",
          area: "Sustained fluency",
          skill: "Rhythm and endurance",
          severity:
            fluencyConsistency < 60 || accuracyDrop >= 10 || paceRatio < 0.72
              ? "high"
              : fluencyConsistency < 72 ||
                  accuracyDrop >= 7 ||
                  paceRatio < 0.82
                ? "medium"
                : "low",
          overallConfidence: confidence.score,
          sampleCount: fluency.printableAttempts,
          evidence: [
            `${formatPercent(fluency.consistency)} consistency in the final block`,
            accuracyDrop > 0
              ? `${Math.round(accuracyDrop)}-point accuracy drop from baseline`
              : `${formatPercent(fluency.accuracy)} accuracy held through the final block`,
          ],
          details:
            "Pace or accuracy became less stable during the longer passage.",
          estimatedImpact:
            "More even rhythm improves long-form writing, notes, chat, and sustained keyboard work.",
          recommendation:
            "Use a rhythm-focused Professional session and hold one comfortable pace from start to finish.",
          professionalSkillFocus: "general",
          professionalGeneralFocus: "rhythm",
          gameDirective:
            "Endurance wave: choose warm-up pressure and build a steady combo.",
          deficit: Math.max(
            78 - fluencyConsistency,
            accuracyDrop,
            (1 - paceRatio) * 100,
          ),
        }),
      );
    }
  }

  const characterOpportunity = repeatedCharacterOpportunity(
    completedBlocks,
    confidence.score,
  );
  if (characterOpportunity) {
    opportunities.push(characterOpportunity);
  }

  opportunities.sort((left, right) => right.priority - left.priority);

  if (opportunities.length === 0) {
    if (input.aggregate.printableAttempts === 0) {
      opportunities.push(
        createOpportunity({
          id: "complete-typed-diagnostic",
          area: "Complete a typed diagnostic",
          skill: "Evidence quality",
          severity: "low",
          overallConfidence: 0,
          sampleCount: 0,
          evidence: [
            "No eligible printable keystrokes were recorded",
            "Pasted or automated text is excluded from typing evidence",
          ],
          details:
            "The diagnostic needs real keystrokes before it can identify a responsible practice priority.",
          estimatedImpact:
            "A typed sample unlocks trustworthy accuracy, pace, rhythm, and key-level evidence.",
          recommendation:
            "Retake the diagnostic and type each block directly instead of pasting text.",
          professionalSkillFocus: "general",
          professionalGeneralFocus: "accuracy",
          gameDirective:
            "Warm-up wave: type targets directly to establish a clean starting signal.",
          deficit: 0,
        }),
      );
    } else {
      opportunities.push(
        createOpportunity({
          id: "extend-fluency",
          area: "Extend sustained fluency",
          skill: "Strong-foundation progression",
          severity: "low",
          overallConfidence: confidence.score,
          sampleCount: input.aggregate.printableAttempts,
          evidence: [
            `${formatPercent(input.aggregate.accuracy)} overall accuracy`,
            `${Math.round(valueOr(input.aggregate.accurateWpm, 0))} accurate WPM across ${Math.round(input.aggregate.activeDurationMs / 1000)} active seconds`,
          ],
          details:
            "No tested area produced a strong negative signal. The best next step is to extend duration or add content complexity gradually.",
          estimatedImpact:
            "Longer clean sessions can build dependable real-world endurance without sacrificing control.",
          recommendation:
            "Use a longer Professional session with varied natural text, then reassess after several sessions.",
          professionalSkillFocus: "general",
          professionalGeneralFocus: "rhythm",
          gameDirective:
            "Fluency challenge: use standard pressure and protect accuracy as the wave grows.",
          deficit: 0,
        }),
      );
    }
  }

  const strengths = completedBlocks
    .map((block) =>
      strengthFromBlock(block, input.aggregate.accurateWpm),
    )
    .filter((strength): strength is AssessmentStrength => strength !== null)
    .slice(0, 3);

  const durationSeconds = Math.round(input.aggregate.activeDurationMs / 1000);
  const detail =
    confidence.band === "high"
      ? `${durationSeconds} active seconds and ${input.aggregate.printableAttempts} attempts across ${completedBlocks.length} blocks provide a dependable practice direction.`
      : confidence.band === "medium"
        ? `${durationSeconds} active seconds across ${completedBlocks.length} blocks are enough for targeted practice, but the result should be confirmed after more sessions.`
        : `This ${durationSeconds}-second sample is useful for exploration only. Complete more of the diagnostic before treating any signal as established.`;

  return {
    confidence: confidence.band,
    confidenceScore: round(confidence.score, 3),
    confidenceLabel: confidenceCopy(confidence.band),
    confidenceDetail: detail,
    topOpportunity: opportunities[0]!,
    opportunities: opportunities.slice(0, 4),
    strengths,
    reassessmentGuidance:
      confidence.band === "high"
        ? "Practice the top opportunity for 3–5 focused sessions, then reassess in several days to see whether the signal moves."
        : "Treat this as a starting hypothesis. Complete a few focused sessions and a full two-minute diagnostic before comparing progress.",
  };
}
